<template>
	<div class="d-flex flex-column overflow-y-auto">
		<div class="flex-grow-1 flex-shrink-1 overflow-y-auto cart-items">
			<v-list v-if="cart !== null && cart.positions.length > 0" class="py-0">
				<template v-for="(pos, i) in cart.positions">
					<v-divider v-if="i !== 0" :key="`${pos.id}-divider`"/>
					<template v-if="pos.frameAssembly === null">
						<CartPositionEntry :key="`${pos.id}-entry`" :position="pos" :show-customer-references="showCustomerReferences" />
					</template>
					<template v-else>
						<CartPositionFrameAssemblyEntry :key="`${pos.id}-entry`" :position="pos" :show-customer-references="showCustomerReferences" />
					</template>
				</template>

				<template v-if="cart.promotionalItem !== null">
					<v-divider/>
					<v-list-item class="px-3">
						<v-list-item-avatar class="mr-2">
							<AsiAvatar :icon="icons.favorite" bg-color="success" icon-color="white" :size="32"/>
						</v-list-item-avatar>
						<v-list-item-content>
							<v-list-item-subtitle class="d-flex flex-row justify-space-between align-center">
								<span class="body-1 black--text">
									{{ itemTitle(cart.promotionalItem.item) }}
								</span>
								<span>
									{{ $t('item.promotionalItem.terms.free') }}
								</span>
							</v-list-item-subtitle>
						</v-list-item-content>
					</v-list-item>
				</template>

				<template v-if="trimmingCost.qty > 0">
					<v-divider/>
					<v-list-item class="px-3">
						<v-list-item-content>
							<v-list-item-subtitle class="d-flex flex-row justify-space-between align-center">
								<span>{{ $t('cart.terms.trimmingCost') }} {{ trimmingCost.qty }}x</span>
								<span>
									{{
										$n(trimmingCost.totalAmount, {
											key: 'currencyDisplay',
											currency: trimmingCost.currencyCode
										})
									}}
								</span>
							</v-list-item-subtitle>
						</v-list-item-content>
					</v-list-item>
				</template>

				<template v-if="cart.minimumOrderSurcharge !== null">
					<v-divider/>
					<v-list-item class="px-3">
						<v-list-item-content>
							<v-list-item-subtitle class="d-flex flex-row justify-space-between align-center">
								<span>{{ $t('cart.minimumOrderSurcharge') }}</span>
								<span>
									{{
										$n(finalAmount(cart.minimumOrderSurcharge.price), {
											key: 'currencyDisplay',
											currency: cart.minimumOrderSurcharge.price.currency.currencyCode
										})
									}}
								</span>
							</v-list-item-subtitle>
						</v-list-item-content>
					</v-list-item>
				</template>

				<template v-if="cart.bulkyGoodsSurcharge !== null && showShipment  && cart.shipmentType !== null">
					<v-divider/>
					<v-list-item class="px-3">
						<v-list-item-content>
							<v-list-item-subtitle class="d-flex flex-row justify-space-between align-center">
								<span>{{ $t('cart.bulkyGoodsSurcharge') }}</span>
								<span>
									{{
										$n(finalAmount(cart.bulkyGoodsSurcharge.price), {
											key: 'currencyDisplay',
											currency: cart.bulkyGoodsSurcharge.price.currency.currencyCode
										})
									}}
								</span>
							</v-list-item-subtitle>
						</v-list-item-content>
					</v-list-item>
				</template>

				<template v-if="showShipment && cart.shipmentType !== null">
					<v-divider/>
					<v-list-item class="px-3">
						<v-list-item-content>
							<v-list-item-subtitle class="d-flex flex-column">
								<div class="d-flex flex-row justify-space-between align-center">
									<span>
										{{ $t('shipmentType.postage') }} ({{ shipmentTypeLabel }})
									</span>
									<span>
										{{
											$n(finalAmount(cart.shipmentType.postage), {
												key: 'currencyDisplay',
												currency: cart.shipmentType.postage.currency.currencyCode
											})
										}}
									</span>
								</div>
								<span v-if="cart.shipmentType.francoLimit !== null && totalAmount[0].amount >= cart.shipmentType.francoLimit.amount" class="mt-3 d-flex flex-row align-center">
									<v-icon size="1rem" class="mr-1">{{ icons.shipping }}</v-icon>
									{{
										$t('shipmentType.noPostageCharged', {
											amount:
												$n(finalAmount(cart.shipmentType.francoLimit), {
													key: 'currencyDisplay',
													currency: cart.shipmentType.francoLimit.currency.currencyCode
												})
										})
									}}
								</span>
							</v-list-item-subtitle>
						</v-list-item-content>
					</v-list-item>
				</template>
			</v-list>
			<div v-else class="flex-grow-1 flex-shrink-1 pa-6 text-center grey--text">
				{{ $t('cart.terms.noActiveOrEmptyCart') }}
			</div>
		</div>

		<CartTotalEntries :cart="cart" bg-color="grey lighten-4"
		                  :include-shipment="showShipment"
		                  :include-vat="amountsInclVat"/>

		<div v-if="!hideCheckoutButton" class="flex-grow-0 flex-shrink-0">
			<v-divider class="grey lighten-1"/>
			<div class=" pa-3 grey lighten-2">
				<AsiBtn :disabled="!canOrder" @click="showCheckout" block color="primary">
					{{ $t('cart.terms.orderNow') }}
				</AsiBtn>
			</div>
		</div>

		<CartPositionBulkOfferRequestDialog v-if="cart !== null && bulkOfferActivePosition !== null && bulkOfferDialog.isLoaded"
		                                    :open="bulkOfferDialog.isOpen"
		                                    :cart="cart"
		                                    :position="bulkOfferActivePosition"
		                                    @cancel="closeBulkOfferDialog"
		                                    @save="bulkOfferRequested"/>
	</div>
</template>

<script lang="ts">
	import {Component, Prop, Watch} from "vue-property-decorator";
	import {mixins} from "vue-class-component";
	import RightChecks from "@/mixins/RightChecks.vue";
	import AsiConfirmDialog from "@/components/common/AsiConfirmDialog.vue";
	import ItemQuantitySelection from "@/components/item/shop/ItemQuantitySelection.vue";
	import AsiBtn from "@/components/common/AsiBtn.vue";
	import AsiAvatar from "@/components/common/AsiAvatar.vue";
	import Icon from "@/plugins/icons";
	import {ICartShopListEntry} from "@/models/cart/CartShopModels";
	import CartHelper from "@/helpers/CartHelper";
	import {ICartPositionShopSimple} from "@/models/cart-position/CartPositionShopModels";
	import {IItemShopSimple} from "@/models/item/ItemShopModels";
	import TranslatedValueHelper from "@/models/translated-value/TranslatedValueHelper";
	import VueI18n from "vue-i18n";
	import {Routes} from "@/helpers/constants";
	import AsiTextFieldSimple from "@/components/common/AsiTextFieldSimple";
	import CartPositionCustomerReferenceInput from "@/components/cart-position/CartPositionCustomerReferenceInput.vue";
	import CartTotalEntries from "@/components/cart/shop/CartTotalEntries.vue";
	import {IPrice} from "@/models/price/PriceModels";
	import TotalHelper, {CurrencyAmount} from "@/helpers/TotalHelper";
	import FrameAssemblyUpdateDialog from "@/components/frame-assembly/FrameAssemblyUpdateDialog.vue";
	import UserUpdateDialogName from "@/components/user/UserUpdateDialogName.vue";
	import ItemHelper from "@/models/item/ItemHelper";
	import FrameAssemblyUpdateButton from "@/components/frame-assembly/FrameAssemblyUpdateButton.vue";
	import ItemChipNumber from "@/components/item/ItemChipNumber.vue";
	import ItemChipAltNumber from "@/components/item/ItemChipAltNumber.vue";
	import TranslateResult = VueI18n.TranslateResult;
	import ItemAttributes from "@/components/item/ItemAttributes.vue";
	import CartFrameAssemblyPositionNrInput
		from "@/components/cart-position/CartFrameAssemblyPositionNrInput.vue";
	import CartPositionEntry from "@/components/cart-position/CartPositionEntry.vue";
	import CartPositionFrameAssemblyEntry from "@/components/cart-position/CartPositionFrameAssemblyEntry.vue";
	import CartPositionBulkOfferRequestDialog
		from "@/components/cart-position/CartPositionBulkOfferRequestDialog.vue";
	import Snackbar from "@/helpers/Snackbar";
	import DialogHandler from "@/components/common/DialogHandler";

	@Component({
		components: {
			CartPositionBulkOfferRequestDialog,
			CartPositionFrameAssemblyEntry,
			CartPositionEntry,
			CartPosition: CartPositionEntry,
			CartFrameAssemblyCustomerReferenceInput: CartFrameAssemblyPositionNrInput,
			ItemAttributes,
			ItemChipAltNumber, ItemChipNumber,
			FrameAssemblyUpdateButton,
			UserUpdateDialogName,
			FrameAssemblyUpdateDialog,
			CartTotalEntries,
			CartPositionCustomerReferenceInput,
			AsiTextFieldSimple, AsiAvatar, AsiBtn, ItemQuantitySelection, AsiConfirmDialog
		}
	})
	export default class CartContent extends mixins(RightChecks) {

		@Prop({required: true})
		public id!: string | null;

		@Prop({type: Boolean, default: false})
		public hideCheckoutButton!: boolean;

		@Prop({type: Boolean, default: false})
		public showShipment!: boolean;

		@Prop({type: Boolean, default: false})
		public showCustomerReferences!: boolean;

		private icons = Icon;
		private bulkOfferDialog: DialogHandler = new DialogHandler(
			() => this.bulkOfferDialogUnloaded(),
		);
		private bulkOfferActivePosition: ICartPositionShopSimple | null = null;

		public mounted(): void {
			this.$nextTick(this.showNextBulkOfferDialog);
		}

		private get cart(): ICartShopListEntry | null {
			return this.id === null ? null : this.$store.getters['cart/cartById'](this.id);
		}

		private get amountsInclVat(): boolean {
			return this.$store.state.ui.amountsInclVat;
		}

		private set amountsInclVat(value: boolean) {
			this.$store.commit('ui/setAmountsInclVat', value);
		}

		private get shipmentTypeLabel(): string | TranslateResult | null {
			if (this.cart === null || this.cart.shipmentType === null) return null;
			return TranslatedValueHelper.get(this.cart.shipmentType.name, this.$i18n.locale, true);
		}

		private get canOrder(): boolean {
			return this.cart !== null
				&& this.cart.positions.length > 0
				&& !this.cart.positions.some(pos => pos.price === null);
		}

		@Watch('cart', {deep: true})
		private onCartChanged(): void {
			this.$nextTick(this.showNextBulkOfferDialog);
		}

		private get trimmingCost(): { qty: number, totalAmount: number, currencyCode: string | null } {
			if (this.cart === null || this.cart.positions.length < 1) {
				return {qty: 0, totalAmount: 0, currencyCode: null};
			}

			const trimmingCostPrices = (this.cart.positions
				.map(pos => pos.trimmingCost)
				.filter(tc => tc !== null) as IPrice[]);

			if (trimmingCostPrices.length < 1) {
				return {qty: 0, totalAmount: 0, currencyCode: null};
			}

			const currency = trimmingCostPrices[0].currency;
			const trimmingCostAmounts = trimmingCostPrices.map(tc => this.finalAmount(tc));

			const qty = trimmingCostAmounts.length;
			const amount = trimmingCostAmounts.reduce((acc: number, cur: number) => acc + cur, 0);

			return {qty: qty, totalAmount: amount, currencyCode: currency.currencyCode};
		}

		private get totalAmount(): CurrencyAmount[] | null {
			if (this.cart === null) {
				return null;
			}

			return TotalHelper.totalExclVat(this.cart, false);
		}

		private itemTitle(item: IItemShopSimple): string | TranslateResult | null {
			return TranslatedValueHelper.get(item.name, this.$i18n.locale);
		}

		private showCheckout(): void {
			if (this.$route.name === Routes.checkout || this.cart === null) return;
			this.$router.push(CartHelper.checkoutRoute(this.cart.id));
		}

		private showNextBulkOfferDialog(): void {
			if (this.cart === null || this.bulkOfferDialog.isLoaded) return;

			const position = this.cart.positions.find(pos => pos.canRequestBulkOffer && !pos.bulkOfferDeclined) ?? null;
			if (position === null) return;

			this.bulkOfferActivePosition = position;
			this.bulkOfferDialog.open();
		}

		private closeBulkOfferDialog(): void {
			this.bulkOfferDialog.close();
		}

		private bulkOfferDialogUnloaded(): void {
			this.bulkOfferActivePosition = null;
			this.$nextTick(this.showNextBulkOfferDialog);
		}

		private bulkOfferRequested(): void {
			Snackbar.show(this.$t('cart.bulkOffer.success'), 'success');
			this.closeBulkOfferDialog();
		}

		private showItem(position: ICartPositionShopSimple): void {
			if (this.$router.resolve(ItemHelper.detailRoute(position.item.itemNumber)).route.path === this.$route.path) return;
			this.$router.push(ItemHelper.detailRoute(position.item.itemNumber));
		}

		private finalAmount(price: IPrice): number {
			return price.amount + (this.amountsInclVat ? TotalHelper.vatAmount(price) : 0);
		}
	}
</script>

<style lang="scss" scoped>
	@import '../../../scss/global';

	.cart-items {
		@include asiScrollbar();

		.item-pos:hover {
			color: var(--v-primary-base);
			cursor: pointer;
		}

		.item-pos-chip-gap {
			gap: 0.5rem;
		}
	}
</style>
